/**********************************************************************
 * ScheduleModal.jsx
 * – Modale di dettaglio di uno scheduling (SOLO visualizzazione)
 *********************************************************************/
import React, { useState, useEffect } from "react";
import ReactDOM              from "react-dom";
import { Document, Packer, Paragraph, TextRun, HeadingLevel, BorderStyle, AlignmentType } from "docx";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import PersonDetailModal from "../People/PersonDetailModal";
import api from "../../../services/api";
import "../../../css/scheduleModal.css";

const ScheduleModal = ({ sch, onClose }) => {
  const [detailPerson, setDetailPerson] = useState(null);

  /* ------------------------------------------------------------------
   * Dati utili
   * ---------------------------------------------------------------- */
  const prog         = sch.programData || {};
  const priority     = sch.priority ?? 0;
  const fmtTime   = t => t.slice(0, 5);
  const fmtDate   = d =>
    new Date(d).toLocaleDateString("it-IT", {
      weekday: "short",
      day    : "numeric",
      month  : "short",
      year   : "numeric",
    });

  const progGuests  = prog.expectedGuests || [];
  const schGuests   = sch.guests          || [];
  const progTopics  = prog.topics         || [];
  const schTopics   = sch.topics          || [];
  const slots       = sch.activeSlots || sch.timeSlots || [];

  // Protagonisti: occurrence override > program fallback
  const occurrenceProtagonists = sch.activeOccurrence?.protagonists || [];
  const programProtagonists = prog.protagonists || [];
  const activeProtagonists = occurrenceProtagonists.length > 0
    ? occurrenceProtagonists
    : programProtagonists;
  const isProtagonistsOverridden = occurrenceProtagonists.length > 0;

  /* blocca lo scroll dello sfondo */
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = original; };
  }, []);

  /* ------------------------------------------------------------------
   * Carica persona completa con qualifiche
   * ---------------------------------------------------------------- */
  const handlePersonClick = async (person) => {
    try {
      const response = await api.get(`/people/${person.id}`);
      setDetailPerson(response.data);
    } catch (error) {
      console.error('Error loading person details:', error);
      setDetailPerson(person);
    }
  };

  /* ------------------------------------------------------------------
   * Carica qualifiche per tutte le persone del documento
   * ---------------------------------------------------------------- */
  const loadAllPeopleWithQualifications = async () => {
    // Raccogli tutti gli ID univoci delle persone
    const peopleIds = new Set();

    // Protagonisti
    activeProtagonists.forEach(p => peopleIds.add(p.id));

    // Ospiti programma e scheduling
    progGuests.forEach(g => peopleIds.add(g.id));
    schGuests.forEach(g => peopleIds.add(g.id));

    // Persone nelle fasce orarie
    slots.forEach(slot => {
      if (Array.isArray(slot.people)) {
        slot.people.forEach(p => peopleIds.add(p.id));
      }
      if (Array.isArray(slot.companyPeople)) {
        slot.companyPeople.forEach(link => {
          if (link.person) peopleIds.add(link.person.id);
        });
      }
    });

    // Carica i dettagli completi per ogni persona
    const peopleMap = {};
    await Promise.all(
      Array.from(peopleIds).map(async (id) => {
        try {
          const response = await api.get(`/people/${id}`);
          peopleMap[id] = response.data;
        } catch (error) {
          console.error(`Error loading person ${id}:`, error);
        }
      })
    );

    return peopleMap;
  };

  /* ------------------------------------------------------------------
   * Formatta qualifiche per visualizzazione
   * ---------------------------------------------------------------- */
  const formatQualifications = (qualifications) => {
    if (!qualifications || qualifications.length === 0) return '';
    return qualifications.map(q => q.name).join(', ');
  };

  /* ------------------------------------------------------------------
   * Genera e scarica la scaletta in .docx
   * ---------------------------------------------------------------- */
  const downloadDocx = async () => {
    // Carica qualifiche per tutte le persone
    const peopleMap = await loadAllPeopleWithQualifications();
    const children = [];

    // Titolo principale
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: prog.title || "Programma",
            bold: true,
            size: 48,
          }),
        ],
        heading: HeadingLevel.TITLE,
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
      })
    );

    // Orario e data
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `${fmtTime(sch.startTime)} – ${fmtTime(sch.endTime)}`,
            bold: true,
            size: 28,
          }),
          new TextRun({
            text: `  |  ${fmtDate(sch.date || new Date())}`,
            size: 28,
          }),
          new TextRun({
            text: `  |  Priorità: P${priority}`,
            size: 24,
            color: "666666",
          }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 },
      })
    );

    // Linea separatrice
    children.push(
      new Paragraph({
        border: {
          bottom: { color: "000000", space: 1, style: BorderStyle.SINGLE, size: 6 },
        },
        spacing: { after: 300 },
      })
    );

    // Helper per sezione titolo
    const addSectionTitle = (title, keepNext = true) => {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: title, bold: true, size: 32 }),
          ],
          spacing: { before: 350, after: 180 },
          keepNext: keepNext,
        })
      );
    };

    // Helper per bullet point
    const addBullet = (text, indent = 0, keepNext = false) => {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: `• ${text}`, size: 26 })],
          indent: { left: 400 + indent * 200 },
          spacing: { after: 100 },
          keepNext: keepNext,
        })
      );
    };

    // Protagonisti
    if (activeProtagonists.length > 0) {
      addSectionTitle("PROTAGONISTI");
      activeProtagonists.forEach((p) => {
        const fullPerson = peopleMap[p.id] || p;
        const phoneText = fullPerson.phone ? ` - ${fullPerson.phone}` : '';
        const qualifications = formatQualifications(fullPerson.qualifications);

        // Nome e telefono
        children.push(
          new Paragraph({
            children: [new TextRun({ text: `• ${fullPerson.nome} ${fullPerson.cognome}${phoneText}`, size: 26, bold: true })],
            indent: { left: 400 },
            spacing: { after: qualifications ? 50 : 100 },
            keepNext: !!qualifications,
            keepLines: true,
          })
        );

        // Qualifiche sotto (se presenti)
        if (qualifications) {
          children.push(
            new Paragraph({
              children: [new TextRun({ text: qualifications, size: 24, italics: true, color: "555555" })],
              indent: { left: 600 },
              spacing: { after: 100 },
              keepLines: true,
            })
          );
        }
      });
    }

    // Nota giornata
    if (sch.occurrenceNote) {
      addSectionTitle("NOTA GIORNATA");
      children.push(
        new Paragraph({
          children: [new TextRun({ text: sch.occurrenceNote, size: 26, italics: true })],
          indent: { left: 400 },
          spacing: { after: 180 },
        })
      );
    }

    // Helper per aggiungere persona con qualifiche
    const addPersonWithQualifications = (person, indent = 0) => {
      const fullPerson = peopleMap[person.id] || person;
      const phoneText = fullPerson.phone ? ` - ${fullPerson.phone}` : '';
      const qualifications = formatQualifications(fullPerson.qualifications);

      children.push(
        new Paragraph({
          children: [new TextRun({ text: `• ${fullPerson.nome} ${fullPerson.cognome}${phoneText}`, size: 26 })],
          indent: { left: 400 + indent * 200 },
          spacing: { after: qualifications ? 50 : 100 },
          keepNext: !!qualifications,
          keepLines: true,
        })
      );

      if (qualifications) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: qualifications, size: 24, italics: true, color: "555555" })],
            indent: { left: 600 + indent * 200 },
            spacing: { after: 100 },
            keepLines: true,
          })
        );
      }
    };

    // Ospiti programma
    if (progGuests.length > 0) {
      addSectionTitle("OSPITI PROGRAMMA");
      progGuests.forEach((g) => addPersonWithQualifications(g));
    }

    // Ospiti scheduling
    if (schGuests.length > 0) {
      addSectionTitle("OSPITI SCHEDULING");
      schGuests.forEach((g) => addPersonWithQualifications(g));
    }

    // Topics
    const allTopics = [...progTopics, ...schTopics];
    if (allTopics.length > 0) {
      addSectionTitle("TOPICS");
      allTopics.forEach((t, i) => addBullet(t.name, 0, i < allTopics.length - 1));
    }

    // Fasce orarie
    if (slots.length > 0) {
      children.push(
        new Paragraph({
          border: {
            bottom: { color: "000000", space: 1, style: BorderStyle.SINGLE, size: 6 },
          },
          spacing: { before: 400, after: 200 },
        })
      );

      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: "SCALETTA FASCE ORARIE", bold: true, size: 36 }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 350 },
        })
      );

      slots.forEach((slot, idx) => {
        const slotStart = slot.startTime?.slice(0, 5) || "??:??";
        const slotEnd = slot.endTime?.slice(0, 5) || "??:??";

        // Calcola contenuto della fascia per decidere se fare page break
        const slotContent = [];

        // Header fascia
        slotContent.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `${idx + 1}. `,
                bold: true,
                size: 28,
              }),
              new TextRun({
                text: `${slotStart} - ${slotEnd}`,
                bold: true,
                size: 30,
              }),
              ...(slot.isEditorial ? [new TextRun({ text: "  [REDAZIONALE]", bold: true, size: 24, color: "FF8C00" })] : []),
            ],
            spacing: { before: 300, after: 120 },
            border: {
              bottom: { color: "CCCCCC", space: 1, style: BorderStyle.SINGLE, size: 2 },
            },
            keepNext: true,
          })
        );

        // Nota fascia
        if (slot.note) {
          slotContent.push(
            new Paragraph({
              children: [
                new TextRun({ text: "Nota: ", bold: true, size: 26 }),
                new TextRun({ text: slot.note, size: 26, italics: true, color: "555555" }),
              ],
              indent: { left: 400 },
              spacing: { after: 120 },
              keepNext: true,
            })
          );
        }

        // Ospiti fascia
        if (Array.isArray(slot.people) && slot.people.length > 0) {
          slotContent.push(
            new Paragraph({
              children: [new TextRun({ text: "Ospiti:", bold: true, size: 28 })],
              indent: { left: 400 },
              spacing: { before: 120, after: 60 },
              keepNext: true,
            })
          );
          slot.people.forEach((person) => {
            const fullPerson = peopleMap[person.id] || person;
            const phoneText = fullPerson.phone ? ` - ${fullPerson.phone}` : '';
            const qualifications = formatQualifications(fullPerson.qualifications);

            slotContent.push(
              new Paragraph({
                children: [new TextRun({ text: `• ${fullPerson.nome} ${fullPerson.cognome}${phoneText}`, size: 26 })],
                indent: { left: 600 },
                spacing: { after: qualifications ? 50 : 100 },
                keepNext: !!qualifications,
                keepLines: true,
              })
            );

            if (qualifications) {
              slotContent.push(
                new Paragraph({
                  children: [new TextRun({ text: qualifications, size: 24, italics: true, color: "555555" })],
                  indent: { left: 800 },
                  spacing: { after: 100 },
                  keepLines: true,
                })
              );
            }
          });
        }

        // Topics fascia
        if (Array.isArray(slot.topics) && slot.topics.length > 0) {
          slotContent.push(
            new Paragraph({
              children: [new TextRun({ text: "Topics:", bold: true, size: 28 })],
              indent: { left: 400 },
              spacing: { before: 120, after: 60 },
              keepNext: true,
            })
          );
          slot.topics.forEach((topic, i) => {
            slotContent.push(
              new Paragraph({
                children: [new TextRun({ text: `• ${topic.name}`, size: 26 })],
                indent: { left: 600 },
                spacing: { after: 80 },
                keepNext: i < slot.topics.length - 1,
              })
            );
          });
        }

        // Rubrica fascia
        if (slot.rubrica) {
          slotContent.push(
            new Paragraph({
              children: [
                new TextRun({ text: "Rubrica: ", bold: true, size: 26 }),
                new TextRun({ text: slot.rubrica.name, size: 26 }),
              ],
              indent: { left: 400 },
              spacing: { before: 120, after: 80 },
            })
          );
        }

        // Tematiche fascia
        if (slot.tematiche) {
          slotContent.push(
            new Paragraph({
              children: [
                new TextRun({ text: "Tematiche: ", bold: true, size: 26 }),
                new TextRun({ text: slot.tematiche, size: 26, italics: true }),
              ],
              indent: { left: 400 },
              spacing: { before: 120, after: 80 },
            })
          );
        }

        // Aziende fascia
        const companyGroups = (slot.companyPeople || []).reduce((acc, link) => {
          const companyId = link.company?.id || link.companyId;
          if (!companyId) return acc;
          if (!acc[companyId]) {
            acc[companyId] = { company: link.company, people: [] };
          }
          if (link.person) {
            acc[companyId].people.push(link.person);
          }
          return acc;
        }, {});

        if (Object.keys(companyGroups).length > 0) {
          slotContent.push(
            new Paragraph({
              children: [new TextRun({ text: "Aziende:", bold: true, size: 28 })],
              indent: { left: 400 },
              spacing: { before: 120, after: 60 },
              keepNext: true,
            })
          );
          const groupValues = Object.values(companyGroups);
          groupValues.forEach((group) => {
            slotContent.push(
              new Paragraph({
                children: [new TextRun({ text: `${group.company?.name || 'Azienda'}:`, bold: true, size: 26 })],
                indent: { left: 600 },
                spacing: { after: 60 },
                keepNext: true,
              })
            );
            group.people.forEach((person) => {
              const fullPerson = peopleMap[person.id] || person;
              const phoneText = fullPerson.phone ? ` - ${fullPerson.phone}` : '';
              const qualifications = formatQualifications(fullPerson.qualifications);

              slotContent.push(
                new Paragraph({
                  children: [new TextRun({ text: `• ${fullPerson.nome} ${fullPerson.cognome}${phoneText}`, size: 26 })],
                  indent: { left: 800 },
                  spacing: { after: qualifications ? 50 : 100 },
                  keepNext: !!qualifications,
                  keepLines: true,
                })
              );

              if (qualifications) {
                slotContent.push(
                  new Paragraph({
                    children: [new TextRun({ text: qualifications, size: 24, italics: true, color: "555555" })],
                    indent: { left: 1000 },
                    spacing: { after: 100 },
                    keepLines: true,
                  })
                );
              }
            });
          });
        }

        // Aggiungi tutto il contenuto della fascia
        children.push(...slotContent);

        // Spazio tra fasce
        children.push(
          new Paragraph({
            spacing: { after: 250 },
          })
        );
      });
    }

    // Footer
    children.push(
      new Paragraph({
        border: {
          top: { color: "000000", space: 1, style: BorderStyle.SINGLE, size: 6 },
        },
        spacing: { before: 400, after: 150 },
      })
    );
    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: `Generato il ${new Date().toLocaleString("it-IT")}`, size: 18, color: "888888" }),
        ],
        alignment: AlignmentType.CENTER,
      })
    );

    // Crea documento
    const doc = new Document({
      sections: [{
        properties: {
          page: {
            margin: { top: 720, right: 720, bottom: 720, left: 720 },
          },
        },
        children,
      }],
    });

    // Genera e scarica
    const blob = await Packer.toBlob(doc);
    const d = sch.date ? new Date(sch.date) : new Date();
    const dateStr = `${String(d.getDate()).padStart(2, "0")}_${String(d.getMonth() + 1).padStart(2, "0")}_${String(d.getFullYear()).slice(-2)}`;
    const startTime = fmtTime(sch.startTime).replace(":", "_");
    const endTime = fmtTime(sch.endTime).replace(":", "_");
    const progName = (prog.title || "programma").replace(/[^a-zA-Z0-9]/g, "_");
    const fileName = `${progName}_${dateStr}_da_${startTime}_a_${endTime}.docx`;
    saveAs(blob, fileName);
  };

  /* ------------------------------------------------------------------
   * Genera e scarica la scaletta in .pdf
   * ---------------------------------------------------------------- */
  const downloadPdf = async () => {
    // Carica qualifiche per tutte le persone
    const peopleMap = await loadAllPeopleWithQualifications();

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    let y = margin;

    const checkPageBreak = (neededHeight) => {
      if (y + neededHeight > pageHeight - margin) {
        pdf.addPage();
        y = margin;
        return true;
      }
      return false;
    };

    // Helper per stampare persona con qualifiche (mantiene insieme)
    const printPersonWithQualifications = (person, indentX, fontSize = 12) => {
      const fullPerson = peopleMap[person.id] || person;
      const phoneText = fullPerson.phone ? ` - ${fullPerson.phone}` : '';
      const qualifications = formatQualifications(fullPerson.qualifications);

      // Calcola altezza necessaria (nome + eventuale riga qualifiche)
      const neededHeight = qualifications ? 10 : 6;
      checkPageBreak(neededHeight);

      pdf.setFontSize(fontSize);
      pdf.setFont("helvetica", "normal");
      pdf.text(`• ${fullPerson.nome} ${fullPerson.cognome}${phoneText}`, indentX, y);
      y += 5;

      if (qualifications) {
        pdf.setFontSize(fontSize - 1);
        pdf.setFont("helvetica", "italic");
        pdf.setTextColor(100);
        pdf.text(qualifications, indentX + 5, y);
        pdf.setTextColor(0);
        y += 5;
      }
    };

    // Titolo
    pdf.setFontSize(26);
    pdf.setFont("helvetica", "bold");
    const title = prog.title || "Programma";
    const titleWidth = pdf.getTextWidth(title);
    pdf.text(title, (pageWidth - titleWidth) / 2, y);
    y += 14;

    // Orario e data
    pdf.setFontSize(13);
    pdf.setFont("helvetica", "normal");
    const subTitle = `${fmtTime(sch.startTime)} – ${fmtTime(sch.endTime)}  |  ${fmtDate(sch.date || new Date())}  |  P${priority}`;
    const subTitleWidth = pdf.getTextWidth(subTitle);
    pdf.text(subTitle, (pageWidth - subTitleWidth) / 2, y);
    y += 10;

    // Linea separatrice
    pdf.setLineWidth(0.5);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 12;

    // Helper per sezione
    const addSection = (title, items, formatItem) => {
      if (!items || items.length === 0) return;

      checkPageBreak(25 + items.length * 7);

      pdf.setFontSize(14);
      pdf.setFont("helvetica", "bold");
      pdf.text(title, margin, y);
      y += 7;

      pdf.setFontSize(12);
      pdf.setFont("helvetica", "normal");
      items.forEach(item => {
        checkPageBreak(7);
        const text = formatItem(item);
        pdf.text(`• ${text}`, margin + 5, y);
        y += 6;
      });
      y += 5;
    };

    // Protagonisti
    if (activeProtagonists.length > 0) {
      checkPageBreak(30 + activeProtagonists.length * 12);
      pdf.setFontSize(14);
      pdf.setFont("helvetica", "bold");
      pdf.text("PROTAGONISTI", margin, y);
      y += 8;
      activeProtagonists.forEach(p => printPersonWithQualifications(p, margin + 5, 12));
      y += 5;
    }

    // Nota giornata
    if (sch.occurrenceNote) {
      checkPageBreak(20);
      pdf.setFontSize(14);
      pdf.setFont("helvetica", "bold");
      pdf.text("NOTA GIORNATA", margin, y);
      y += 7;
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "italic");
      const noteLines = pdf.splitTextToSize(sch.occurrenceNote, contentWidth - 5);
      noteLines.forEach(line => {
        checkPageBreak(6);
        pdf.text(line, margin + 5, y);
        y += 6;
      });
      y += 5;
    }

    // Ospiti programma
    if (progGuests.length > 0) {
      checkPageBreak(25 + progGuests.length * 10);
      pdf.setFontSize(14);
      pdf.setFont("helvetica", "bold");
      pdf.text("OSPITI PROGRAMMA", margin, y);
      y += 8;
      progGuests.forEach(g => printPersonWithQualifications(g, margin + 5, 12));
      y += 5;
    }

    // Ospiti scheduling
    if (schGuests.length > 0) {
      checkPageBreak(25 + schGuests.length * 10);
      pdf.setFontSize(14);
      pdf.setFont("helvetica", "bold");
      pdf.text("OSPITI SCHEDULING", margin, y);
      y += 8;
      schGuests.forEach(g => printPersonWithQualifications(g, margin + 5, 12));
      y += 5;
    }

    // Topics
    const allTopics = [...progTopics, ...schTopics];
    addSection("TOPICS", allTopics, t => t.name);

    // Fasce orarie
    if (slots.length > 0) {
      checkPageBreak(20);
      pdf.setLineWidth(0.5);
      pdf.line(margin, y, pageWidth - margin, y);
      y += 10;

      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      const slotsTitle = "SCALETTA FASCE ORARIE";
      const slotsTitleWidth = pdf.getTextWidth(slotsTitle);
      pdf.text(slotsTitle, (pageWidth - slotsTitleWidth) / 2, y);
      y += 12;

      slots.forEach((slot, idx) => {
        // Calcola altezza stimata della fascia (considerando qualifiche)
        let slotHeight = 15;
        if (slot.note) slotHeight += Math.ceil(slot.note.length / 80) * 6 + 3;
        if (slot.people?.length) {
          slotHeight += 8;
          slot.people.forEach(p => {
            const fullPerson = peopleMap[p.id] || p;
            slotHeight += fullPerson.qualifications?.length ? 10 : 6;
          });
        }
        if (slot.topics?.length) slotHeight += 8 + slot.topics.length * 6;
        const companyGroups = (slot.companyPeople || []).reduce((acc, link) => {
          const companyId = link.company?.id || link.companyId;
          if (!companyId) return acc;
          if (!acc[companyId]) acc[companyId] = { company: link.company, people: [] };
          if (link.person) acc[companyId].people.push(link.person);
          return acc;
        }, {});
        const companyCount = Object.keys(companyGroups).length;
        if (companyCount > 0) {
          slotHeight += 8;
          Object.values(companyGroups).forEach(g => {
            slotHeight += 6;
            g.people.forEach(p => {
              const fullPerson = peopleMap[p.id] || p;
              slotHeight += fullPerson.qualifications?.length ? 10 : 6;
            });
          });
        }

        // Se non entra, nuova pagina
        if (y + slotHeight > pageHeight - margin) {
          pdf.addPage();
          y = margin;
        }

        const slotStart = slot.startTime?.slice(0, 5) || "??:??";
        const slotEnd = slot.endTime?.slice(0, 5) || "??:??";

        // Header fascia
        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        let headerText = `${idx + 1}. ${slotStart} - ${slotEnd}`;
        if (slot.isEditorial) headerText += "  [REDAZIONALE]";
        pdf.text(headerText, margin, y);
        y += 6;

        // Linea sotto header
        pdf.setLineWidth(0.2);
        pdf.setDrawColor(200);
        pdf.line(margin, y, pageWidth - margin, y);
        pdf.setDrawColor(0);
        y += 5;

        // Nota
        if (slot.note) {
          pdf.setFontSize(12);
          pdf.setFont("helvetica", "bold");
          pdf.text("Nota:", margin + 5, y);
          y += 6;
          pdf.setFontSize(11);
          pdf.setFont("helvetica", "italic");
          pdf.setTextColor(100);
          const noteLines = pdf.splitTextToSize(slot.note, contentWidth - 10);
          noteLines.forEach(line => {
            pdf.text(line, margin + 10, y);
            y += 5;
          });
          pdf.setTextColor(0);
          y += 3;
        }

        // Ospiti fascia
        if (Array.isArray(slot.people) && slot.people.length > 0) {
          pdf.setFontSize(13);
          pdf.setFont("helvetica", "bold");
          pdf.text("Ospiti:", margin + 5, y);
          y += 6;
          slot.people.forEach(person => printPersonWithQualifications(person, margin + 10, 12));
          y += 3;
        }

        // Topics fascia
        if (Array.isArray(slot.topics) && slot.topics.length > 0) {
          pdf.setFontSize(13);
          pdf.setFont("helvetica", "bold");
          pdf.text("Topics:", margin + 5, y);
          y += 6;
          pdf.setFontSize(12);
          pdf.setFont("helvetica", "normal");
          slot.topics.forEach(topic => {
            pdf.text(`• ${topic.name}`, margin + 10, y);
            y += 5;
          });
          y += 3;
        }

        // Rubrica fascia
        if (slot.rubrica) {
          checkPageBreak(10);
          pdf.setFontSize(13);
          pdf.setFont("helvetica", "bold");
          pdf.text("Rubrica:", margin + 5, y);
          y += 6;
          pdf.setFontSize(12);
          pdf.setFont("helvetica", "normal");
          pdf.text(slot.rubrica.name, margin + 10, y);
          y += 6;
        }

        // Tematiche fascia
        if (slot.tematiche) {
          checkPageBreak(10);
          pdf.setFontSize(13);
          pdf.setFont("helvetica", "bold");
          pdf.text("Tematiche:", margin + 5, y);
          y += 6;
          pdf.setFontSize(12);
          pdf.setFont("helvetica", "italic");
          const tematicheLines = pdf.splitTextToSize(slot.tematiche, contentWidth - 15);
          tematicheLines.forEach(line => {
            pdf.text(line, margin + 10, y);
            y += 5;
          });
          pdf.setFont("helvetica", "normal");
          y += 3;
        }

        // Aziende fascia
        if (companyCount > 0) {
          pdf.setFontSize(13);
          pdf.setFont("helvetica", "bold");
          pdf.text("Aziende:", margin + 5, y);
          y += 6;
          Object.values(companyGroups).forEach(group => {
            pdf.setFontSize(12);
            pdf.setFont("helvetica", "bold");
            pdf.text(`${group.company?.name || 'Azienda'}:`, margin + 10, y);
            y += 5;
            group.people.forEach(person => printPersonWithQualifications(person, margin + 15, 12));
          });
          y += 3;
        }

        y += 8;
      });
    }

    // Footer
    checkPageBreak(15);
    pdf.setLineWidth(0.5);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 8;
    pdf.setFontSize(9);
    pdf.setTextColor(150);
    const footer = `Generato il ${new Date().toLocaleString("it-IT")}`;
    const footerWidth = pdf.getTextWidth(footer);
    pdf.text(footer, (pageWidth - footerWidth) / 2, y);

    // Salva
    const d = sch.date ? new Date(sch.date) : new Date();
    const dateStr = `${String(d.getDate()).padStart(2, "0")}_${String(d.getMonth() + 1).padStart(2, "0")}_${String(d.getFullYear()).slice(-2)}`;
    const startTime = fmtTime(sch.startTime).replace(":", "_");
    const endTime = fmtTime(sch.endTime).replace(":", "_");
    const progName = (prog.title || "programma").replace(/[^a-zA-Z0-9]/g, "_");
    const fileName = `${progName}_${dateStr}_da_${startTime}_a_${endTime}.pdf`;
    pdf.save(fileName);
  };

  /* ------------------------------------------------------------------
   * Render
   * ---------------------------------------------------------------- */
  return ReactDOM.createPortal(
    <div className="schedule-modal-backdrop" onClick={onClose}>
      <div
        className="schedule-modal-content"
        onClick={e => e.stopPropagation()}
      >
        {/* chiusura */}
        <button
          className="schedule-modal-close"
          onClick={onClose}
          aria-label="Chiudi"
        >
          ×
        </button>

        {/* header ---------------------------------------------------- */}
        <div className="schedule-modal-header">
          <img
            className="schedule-modal-thumb"
            src={prog.imageUrl || prog.image_url || "https://via.placeholder.com/80"}
            alt={prog.title || "Titolo non disponibile"}
          />
          <div className="schedule-modal-info">
            <h2 className="schedule-modal-title">
              <span className="badge-priority">P{priority}</span>
              {prog.title || "Titolo non disponibile"}
            </h2>
            <p className="schedule-modal-time">
              {fmtTime(sch.startTime)} – {fmtTime(sch.endTime)} ·{" "}
              {sch.date ? fmtDate(sch.date) : "ricorrente"}
            </p>
          </div>
        </div>

        {/* corpo ------------------------------------------------------ */}
        <div className="schedule-modal-body">

          {prog.description && (
            <section className="schedule-modal-section">
              <h4>Descrizione Programma</h4>
              <p className="schedule-modal-desc">{prog.description}</p>
            </section>
          )}

          {/* protagonisti ------------------------------------------------ */}
          {activeProtagonists.length > 0 && (
            <section className="schedule-modal-section">
              <h4>Protagonisti</h4>
              <div className="schedule-modal-chip-list">
                {activeProtagonists.map(p => (
                  <div
                    key={p.id}
                    className="schedule-modal-chip protagonist-chip"
                    onClick={() => handlePersonClick(p)}
                    style={{ cursor: 'pointer' }}
                    title="Clicca per visualizzare i dettagli"
                  >
                    <img
                      src={p.imageUrl || p.image_url || "https://via.placeholder.com/26"}
                      alt={`${p.nome} ${p.cognome}`}
                    />
                    <span>{p.nome} {p.cognome}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {sch.description && (
            <section className="schedule-modal-section">
              <h4>Dettagli Scheduling</h4>
              <p className="schedule-modal-desc">{sch.description}</p>
            </section>
          )}

          {sch.occurrenceNote && (
            <section className="schedule-modal-section">
              <h4>Nota Giornata</h4>
              <p className="schedule-modal-desc schedule-modal-occurrence-note">{sch.occurrenceNote}</p>
            </section>
          )}

          {/* ospiti --------------------------------------------------- */}
          {progGuests.length > 0 && (
            <section className="schedule-modal-section">
              <h4>Ospiti Programma</h4>
              <div className="schedule-modal-chip-list">
                {progGuests.map(g => (
                  <div
                    key={g.id}
                    className="schedule-modal-chip"
                    onClick={() => handlePersonClick(g)}
                    style={{ cursor: 'pointer' }}
                    title="Clicca per visualizzare i dettagli"
                  >
                    <img
                      src={g.imageUrl || "https://via.placeholder.com/26"}
                      alt={`${g.nome} ${g.cognome}`}
                    />
                    <span>{g.nome} {g.cognome}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {schGuests.length > 0 && (
            <section className="schedule-modal-section">
              <h4>Ospiti Scheduling</h4>
              <div className="schedule-modal-chip-list">
                {schGuests.map(g => (
                  <div
                    key={g.id}
                    className="schedule-modal-chip"
                    onClick={() => handlePersonClick(g)}
                    style={{ cursor: 'pointer' }}
                    title="Clicca per visualizzare i dettagli"
                  >
                    <img
                      src={g.imageUrl || "https://via.placeholder.com/26"}
                      alt={`${g.nome} ${g.cognome}`}
                    />
                    <span>{g.nome} {g.cognome}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* topics --------------------------------------------------- */}
          {progTopics.length > 0 && (
            <section className="schedule-modal-section">
              <h4>Topics Programma</h4>
              <div className="schedule-modal-chip-list">
                {progTopics.map(t => (
                  <div key={t.id} className="schedule-modal-chip topic-chip">
                    <span>{t.name}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {schTopics.length > 0 && (
            <section className="schedule-modal-section">
              <h4>Topics Scheduling</h4>
              <div className="schedule-modal-chip-list">
                {schTopics.map(t => (
                  <div key={t.id} className="schedule-modal-chip topic-chip">
                    <span>{t.name}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {slots.length > 0 && (
            <section className="schedule-modal-section">
              <h4>Fasce Orarie</h4>
              <div className="schedule-modal-slot-list">
                {slots.map((slot, idx) => {
                  const companyGroups = (slot.companyPeople || []).reduce((acc, link) => {
                    const companyId = link.company?.id || link.companyId;
                    if (!companyId) return acc;
                    if (!acc[companyId]) {
                      acc[companyId] = { company: link.company, people: [] };
                    }
                    if (link.person) {
                      acc[companyId].people.push(link.person);
                    }
                    return acc;
                  }, {});
                  return (
                    <div key={slot.id || idx} className="schedule-modal-slot-card">
                      <div className="schedule-modal-slot-header">
                        <div>
                          <strong>{slot.startTime?.slice(0, 5)} - {slot.endTime?.slice(0, 5)}</strong>
                          {slot.note && <p className="schedule-modal-slot-note">{slot.note}</p>}
                        </div>
                        {slot.isEditorial && <span className="badge-editorial">Redazionale</span>}
                      </div>

                      {Array.isArray(slot.people) && slot.people.length > 0 && (
                        <div className="schedule-modal-slot-block">
                          <h5>Ospiti</h5>
                          <div className="schedule-modal-chip-list spaced">
                            {slot.people.map(person => (
                              <div
                                key={person.id}
                                className="schedule-modal-chip"
                                onClick={() => handlePersonClick(person)}
                                style={{ cursor: 'pointer' }}
                                title="Clicca per visualizzare i dettagli"
                              >
                                <img
                                  src={person.imageUrl || person.image_url || 'https://via.placeholder.com/26'}
                                  alt=""
                                />
                                <span>{person.nome} {person.cognome}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {Array.isArray(slot.topics) && slot.topics.length > 0 && (
                        <div className="schedule-modal-slot-block">
                          <h5>Topics</h5>
                          <div className="schedule-modal-chip-list spaced">
                            {slot.topics.map(topic => (
                              <div key={topic.id} className="schedule-modal-chip topic-chip">
                                <span>{topic.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {slot.rubrica && (
                        <div className="schedule-modal-slot-block">
                          <h5>Rubrica</h5>
                          <p className="schedule-modal-slot-note">{slot.rubrica.name}</p>
                        </div>
                      )}

                      {slot.tematiche && (
                        <div className="schedule-modal-slot-block">
                          <h5>Tematiche</h5>
                          <p className="schedule-modal-slot-note">{slot.tematiche}</p>
                        </div>
                      )}

                      {Object.keys(companyGroups).length > 0 && (
                        <div className="schedule-modal-slot-block">
                          <h5>Aziende</h5>
                          <div className="schedule-modal-company-list">
                            {Object.values(companyGroups).map((group, companyIdx) => (
                              <div key={group.company?.id || `company-${companyIdx}`} className="schedule-modal-company">
                                <strong>{group.company?.name || 'Azienda'}</strong>
                                <div className="schedule-modal-chip-list spaced">
                                  {group.people.map(person => (
                                    <div
                                      key={person.id}
                                      className="schedule-modal-chip"
                                      onClick={() => handlePersonClick(person)}
                                      style={{ cursor: 'pointer' }}
                                      title="Clicca per visualizzare i dettagli"
                                    >
                                      <img
                                        src={person.imageUrl || person.image_url || 'https://via.placeholder.com/26'}
                                        alt=""
                                      />
                                      <span>{person.nome} {person.cognome}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Pulsanti download scaletta */}
          <div className="schedule-modal-footer">
            <button
              className="schedule-modal-download-btn pdf"
              onClick={downloadPdf}
              title="Scarica scaletta in formato PDF"
            >
              <i className="fas fa-file-pdf" />
              PDF
            </button>
            <button
              className="schedule-modal-download-btn docx"
              onClick={downloadDocx}
              title="Scarica scaletta in formato Word"
            >
              <i className="fas fa-file-word" />
              DOCX
            </button>
          </div>
        </div>

        {detailPerson && (
          <PersonDetailModal
            person={detailPerson}
            onClose={() => setDetailPerson(null)}
          />
        )}
      </div>
    </div>,
    document.body
  );
};

export default ScheduleModal;
